import fetch from "cross-fetch";
import { ApolloClient, split, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { RetryLink } from "@apollo/client/link/retry";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { ContextSetter, setContext } from "@apollo/client/link/context";
import { WebSocketLink } from "@apollo/client/link/ws";

// state
import { jwt, xsrf } from "@state/local-storage";

// constants
import { clientId } from "@clockwise/web-commons/src/util/fetch.util";
import { genTxnId } from "@clockwise/client-commons/src/util/txn";
import { GRAPHQL_BATCH_URL, GRAPHQL_URL, SUBSCRIPTION_URL } from "./environment";
import { typePolicies } from "./typePolicies";

type NetworkLinkHeaders = {
  Authorization?: string;
  Xsrf?: string;
  txnId: string;
  clientId: string;
};

type NetworkLinkContext = {
  previousHeaders: NetworkLinkHeaders;
};

const formNetworkLink = (graphUrl: string): ContextSetter => (
  _request,
  prevContext: NetworkLinkContext,
) => {
  const _jwt = jwt.get();
  const Authorization = _jwt && `Bearer ${_jwt}`;
  const Xsrf = xsrf.get();
  const txnId = genTxnId();

  const headers: NetworkLinkHeaders = {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    ...prevContext.previousHeaders,
    txnId,
    clientId,
  };

  if (Authorization) {
    headers.Authorization = Authorization;
  }

  if (Xsrf) {
    headers.Xsrf = Xsrf;
  }

  return {
    graphUrl,
    headers,
  };
};

const link = setContext(formNetworkLink(GRAPHQL_URL));
const batchLink = setContext(formNetworkLink(GRAPHQL_BATCH_URL));

/**
 * This url gets overriden by the clockwise network link.
 */
const httpLink = new HttpLink({
  uri: GRAPHQL_URL,
  credentials: "include",
  fetch, // Provide a cross environment version of fetch, so that this can run in both the browser and tests.
});

const batchHttpLink = new BatchHttpLink({
  uri: GRAPHQL_BATCH_URL,
  credentials: "include",
  batchInterval: 50,
  fetch, // Provide a cross environment version of fetch, so that this can run in both the browser and tests.
});

// All queries sent batched, all mutation sent non batched.
const combinedBatchLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === "OperationDefinition" && definition.operation !== "mutation";
  },
  ApolloLink.from([batchLink, batchHttpLink]),
  ApolloLink.from([link, httpLink]),
);

// See https://www.apollographql.com/docs/react/api/link/apollo-link-retry/ for RetryLink's defaults.
const retryLink = new RetryLink({
  // This is the default, and is done with jitter true to add some randomness
  // and an exponential delay increase starting from 30 ms and doubling.
  attempts: { max: 5 },
});

/**
 * Need to let the cache know about our union types.
 * See https://www.apollographql.com/docs/react/advanced/fragments.html#fragment-matcher.
 */
export const cache = new InMemoryCache({ typePolicies });

const wsLink = new WebSocketLink({
  uri: SUBSCRIPTION_URL,
  options: {
    reconnect: true,
    lazy: true,
    connectionParams: () =>
      Promise.all([jwt.get(), xsrf.get()]).then(([jwt, xsrf]) => {
        return {
          Authorization: jwt && `Bearer ${jwt}`,
          Xsrf: xsrf,
        };
      }),
  },
});

const combinedWSLink = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === "OperationDefinition" && definition.operation === "subscription";
  },
  ApolloLink.from([link, wsLink]),
  ApolloLink.from([combinedBatchLink]),
);

const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = JSON.parse(JSON.stringify(operation.variables), (key, value) => {
      return key === "__typename" ? undefined : value;
    });
  }

  if (!forward) {
    return null;
  }

  return forward(operation);
});

const storedTokenHash = {
  jwt,
  xsrf,
};

function updateTokenWithHeaders(headers: Headers) {
  return function updateToken(token: keyof typeof storedTokenHash) {
    const tokenStore = storedTokenHash[token];
    const t = headers.get(token);
    if (t && t !== tokenStore.get()) {
      tokenStore.set(t);
    }
  };
}

// our custom "afterware" that checks each response and sets jwt/xsrf from header
const afterwareLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const updateToken = updateTokenWithHeaders(context.response.headers as Headers);

    // maybe set the jwt
    updateToken("jwt");

    // maybe set the xsrf
    updateToken("xsrf");

    return response;
  });
});

export const client = new ApolloClient({
  cache,
  connectToDevTools: process.env.NODE_ENV !== "production",
  link: ApolloLink.from([afterwareLink, omitTypenameLink, retryLink, combinedWSLink]),
});
